/* eslint-disable indent */
import { ArtworkDetail } from 'components/artwork/ArtworkDetail'
import { ArtworkDetailsSkeleton } from 'components/artwork/ArtworkDetailsSkeleton'
import { CurrencyDisplay } from 'components/common/CurrencyDisplay'
import { Icon } from 'components/common/Icon'
import { Popover } from 'components/common/Popover'
import { Size, SizeDisplay } from 'components/common/SizeDisplay'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { ArtworkRoyalty } from 'types'

type Features = {
  numbered?: boolean
  dated?: boolean
  handFinished?: boolean
  additionFeatures?: string
  signed?: string
  framed?: boolean
}

type Includes = {
  originalPacking?: boolean
  nfcAuthentication?: boolean
  authenticityCertificate?: boolean
}

type Props = {
  date?: string | number
  size?: Size
  material?: string
  medium?: string
  editionSize?: string
  price?: number
  latestSale?: number
  currency?: string
  gallery?: string
  features?: Features
  information?: string
  includes?: Includes
  hideTitle?: boolean
  currentEditionName?: string
  currentEditionOtherName?: string
  royalty?: ArtworkRoyalty
  withLoader?: boolean
  upcomingRelease?: boolean
  upcomingReleaseUser?: boolean
}

export const ArtworkDetails = ({
  date,
  size,
  material,
  medium,
  editionSize,
  price,
  latestSale = 2,
  currency,
  gallery,
  features,
  information,
  includes,
  hideTitle = false,
  currentEditionName = 'Main',
  currentEditionOtherName,
  withLoader = true,
  upcomingRelease = false,
  upcomingReleaseUser = false,
}: Props) => {
  const isLoaded = [
    date,
    size,
    material,
    medium,
    editionSize,
    price,
    gallery,
    features,
    information,
    includes,
    upcomingRelease,
    upcomingReleaseUser,
  ].reduce(
    (prev, curr) =>
      !withLoader ||
      (typeof prev !== 'undefined' && typeof curr !== 'undefined'),
    !withLoader
  )

  const { t } = useTranslation()

  const [priceDifferenceFromLatestSale, setPriceDifferenceFromLatestSale] =
    useState<number>(
      latestSale && price ? ((latestSale - price) / price) * 100 : 0
    )

  useEffect(() => {
    if (!latestSale || !price) {
      setPriceDifferenceFromLatestSale(0)
      return
    }
    setPriceDifferenceFromLatestSale(((latestSale - price) / price) * 100)
  }, [latestSale, price])

  if (!isLoaded) return <ArtworkDetailsSkeleton />

  return (
    <div className='flex flex-col space-y-3'>
      {date && (
        <ArtworkDetail title={t('common:date')} hideTitle={hideTitle}>
          <p>{date}</p>
        </ArtworkDetail>
      )}

      {size && (
        <ArtworkDetail title={t('single_artwork:size')} hideTitle={hideTitle}>
          <SizeDisplay size={size} />
        </ArtworkDetail>
      )}

      {medium && material && (
        <ArtworkDetail
          title={t('single_artwork:material')}
          hideTitle={hideTitle}
        >
          <p>
            {medium} - {material}
          </p>
        </ArtworkDetail>
      )}

      {editionSize && (
        <ArtworkDetail
          title={t('single_artwork:edition_size')}
          hideTitle={hideTitle}
        >
          <p>{editionSize}</p>
        </ArtworkDetail>
      )}

      {price && price > 0 && !isNaN(price) && currency ? (
        <ArtworkDetail
          title={t('single_artwork:release_price')}
          hideTitle={hideTitle}
          toolTip={t('single_artwork:release_price_tooltip')}
        >
          <CurrencyDisplay price={price} currency={currency}>
            {priceDifferenceFromLatestSale !== 0 && (
              <div className='flex items-center'>
                <Icon
                  name='arrow-caret-up'
                  className='text-emerald-900'
                  size={24}
                />
                <span className='text-emerald-900'>
                  {priceDifferenceFromLatestSale}%
                </span>
              </div>
            )}
          </CurrencyDisplay>
        </ArtworkDetail>
      ) : null}

      {gallery && (
        <ArtworkDetail
          title={medium === 'Book' ? 'Publisher' : t('single_artwork:gallery')}
          hideTitle={hideTitle}
        >
          <p>{gallery}</p>
        </ArtworkDetail>
      )}

      {features &&
        ((features.signed && features.signed?.toLowerCase() !== 'no') ||
          features.numbered ||
          features.dated ||
          features.handFinished ||
          (features.additionFeatures &&
            features.additionFeatures?.toLowerCase() !== 'no') ||
          features.framed) && (
          <ArtworkDetail
            title={t('single_artwork:features')}
            hideTitle={hideTitle}
          >
            <div className='-mx-2 -my-0.5 flex flex-wrap items-center'>
              {features.signed && features.signed !== 'NO' && (
                <span className='mx-2 my-0.5 flex items-center'>
                  <Icon
                    name='feather'
                    size={16}
                    className='mr-2 text-cool-500'
                  />
                  {features.signed === 'SIGNED' && 'Signed'}
                  {features.signed === 'DIGITAL_SIGNATURE' &&
                    'Digital Signature'}
                  {features.signed === 'ENGRAVED_SIGNATURE' &&
                    'Engraved Signature'}
                </span>
              )}
              {features.numbered && (
                <span className='mx-2 my-0.5 flex items-center'>
                  {}
                  <Icon
                    name='number-1'
                    size={16}
                    className='mr-2 text-cool-500'
                  />{' '}
                  {currentEditionName === 'Main' &&
                    t('single_artwork:numbered')}
                  {currentEditionName === 'Artist' &&
                    t('single_artwork:numbered_ap')}
                  {currentEditionName === 'Printer' &&
                    t('single_artwork:numbered_pp')}
                  {currentEditionName === 'Other' &&
                    t('single_artwork:numbered_other', {
                      currentEditionOtherName: currentEditionOtherName,
                    })}
                </span>
              )}
              {features.dated && (
                <span className='mx-2 my-0.5 flex items-center'>
                  <Icon
                    name='calendar'
                    size={16}
                    className='mr-2 text-cool-500'
                  />{' '}
                  {t('single_artwork:dated')}
                </span>
              )}
              {features.handFinished && (
                <span className='mx-2 my-0.5 flex items-center'>
                  <Icon
                    name='big-brush'
                    size={16}
                    className='mr-2 text-cool-500'
                  />{' '}
                  {t('single_artwork:hand_finished')}
                </span>
              )}
              {features.framed && (
                <span className='mx-2 my-0.5 flex items-center'>
                  <Icon name='frame' size={16} className='mr-2 text-cool-500' />{' '}
                  {t('single_artwork:framed')}
                </span>
              )}
            </div>
            {features.additionFeatures &&
              features.additionFeatures !== 'No' && (
                <span className='-mx-2 -my-0.5 mt-1 flex items-start'>
                  <Icon
                    name='actions-plus'
                    size={16}
                    className='mx-2 mt-1 text-cool-500'
                  />
                  {features.additionFeatures}
                </span>
              )}
          </ArtworkDetail>
        )}

      {information && (
        <ArtworkDetail
          title={t('single_artwork:information')}
          hideTitle={hideTitle}
        >
          <p>{information}</p>
        </ArtworkDetail>
      )}

      {includes &&
        (includes.authenticityCertificate ||
          includes.originalPacking ||
          includes.nfcAuthentication) && (
          <ArtworkDetail
            title={t('single_artwork:includes')}
            hideTitle={hideTitle}
          >
            <div className='relative -mx-2 -my-0.5 flex flex-wrap'>
              {includes.authenticityCertificate && (
                <span className='relative mx-2 my-0.5 flex items-center space-x-2'>
                  <Icon
                    name='guarantee-1'
                    size={16}
                    className='mr-2 text-cool-500'
                  />
                  {t('single_artwork:certificate_of_authenticity_title')}
                  <Popover
                    content={t(
                      'single_artwork:certificate_of_authenticity_content'
                    )}
                    side='top'
                  />
                </span>
              )}
              {includes.originalPacking && (
                <span className='relative mx-2 my-0.5 flex items-center space-x-2'>
                  <Icon name='box-1' size={16} className='mr-2 text-cool-500' />
                  {t('single_artwork:original_packaging_title')}
                  <Popover
                    content={t('single_artwork:original_packaging_content')}
                    side='top'
                  />
                </span>
              )}
              {includes.nfcAuthentication && (
                <span className='mx-2 my-0.5 flex items-center space-x-2'>
                  <Icon name='nfc' size={16} className='mr-2 text-cool-500' />
                  {t('single_artwork:nfc_authentication_title')}
                  <Popover
                    content={t('single_artwork:nfc_authentication_content')}
                    side='top'
                  />
                </span>
              )}
            </div>
          </ArtworkDetail>
        )}
    </div>
  )
}
